import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { Form } from 'react-final-form';
import type { FormApi } from 'final-form';
import Loader from 'components/Loader';
import { has_permission } from 'config/permissions';
import { DetailHeader } from 'components/Forms/DetailHeader';
import { TabAccordion } from 'components/Forms/TabAccordion';
import { recipientTypes, SubtypeOptions, TypeOptions } from './constants';
import { RecipientAccordion } from './RecipientAccordion';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getRecipientsState } from 'selectors/recipients';
import { createRecipient } from 'store/recipients/actions';
import {
  NonProfitProps,
  DuplicateConflictPayload,
  DuplicateType,
  LookupRecipient,
} from 'store/recipients/types';
import { useNavigate } from 'react-router-dom';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { showToast } from 'components/Toast';
import { getActiveRole } from 'selectors/admins';
import {
  adminRecipientsApi,
  useGetAdminRecipientQuery,
  useLazyLookupRecipientsQuery,
  usePatchAdminRecipientMutation,
} from 'services/adminRecipientsApi';
import {
  SelectedElectionItem,
  useCreateCandidateMutation,
  useCreateRaceMutation,
} from 'services/adminCandidatesApi';
import {
  buildPatch,
  hasChariotKeys,
  subtypeChangedToNonC3,
  einChangedToNonNull,
  parseCreate409,
  validateRecipientForm,
  getTabErrorStatus,
} from './helpers';
import { DuplicateWarningModal } from 'components/Modal/duplicateWarningModal';
import { ChariotConfirmModal } from 'components/Modal/chariotConfirmModal';
import { RecipientFormValues } from './types';
import { trackEvent } from 'helpers/mixpanelEvents';

const trackRecipientSaved = (interactionType: 'create' | 'edit', recipientType: string) =>
  trackEvent('Recipient Saved', {
    screen: 'recipient_detail',
    interaction_type: interactionType,
    recipient_type: recipientType,
  });

type PatchOpts = {
  confirmDuplicate?: boolean;
  clearChariotKeys?: boolean;
  reregisterChariot?: boolean;
};

type DuplicateModalState = {
  isOpen: boolean;
  duplicates: LookupRecipient[];
  duplicateType: DuplicateType;
  pendingValues: RecipientFormValues | null;
  pendingOpts: PatchOpts;
};

const CLOSED_MODAL: DuplicateModalState = {
  isOpen: false,
  duplicates: [],
  duplicateType: 'display_name',
  pendingValues: null,
  pendingOpts: {},
};

type ChariotModalState = {
  type: 'subtype' | 'ein' | null;
  pendingValues: RecipientFormValues | null;
  pendingOpts: PatchOpts;
};

const CLOSED_CHARIOT_MODAL: ChariotModalState = {
  type: null,
  pendingValues: null,
  pendingOpts: {},
};

const buildBody = (values: RecipientFormValues): NonProfitProps => ({
  display_name: values.recipient_name,
  legal_name: values.recipient_legal_name,
  type: values.type,
  subtype: values.subtype,
  photo_id: values.recipient_photo_id,
  de_url_id: values.recipient_de_url_id,
  is_public_searchable: values.public_search,
  ...(values.recipient_ein && { legal_id: values.recipient_ein }),
  ...(values.recipient_contact_name && { primary_contact_name: values.recipient_contact_name }),
  ...(values.recipient_contact_email && { primary_contact_email: values.recipient_contact_email }),
  ...(values.recipient_status && { recipient_status: values.recipient_status }),
  ...(values.stripe_authorized && { is_stripe_authorized: true }),
  ...(values.onboarding_form_complete && { onboarding_form_complete: true }),
  ...(values.de_status && { de_status: values.de_status }),
  ...(values.public_fundraising_query_str && {
    public_fundraising_query_str: values.public_fundraising_query_str,
  }),
});

export const RecipientsDetail = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(location.search);
  const recipientId = urlParams.get('recipient_id');
  const isEditMode = Boolean(recipientId);
  const [itemVisible, setItemVisible] = useState(0);
  const [tabStatus, setTabStatus] = useState(['in-progress']);
  const [currentType, setCurrentType] = useState<string>(TypeOptions[0].value);
  const activeRole = useAppSelector(getActiveRole);
  const recipientsState = useAppSelector(getRecipientsState);

  const {
    data: recipientDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
  } = useGetAdminRecipientQuery(
    { recipientId: recipientId ?? '' },
    { skip: !isEditMode || !activeRole }
  );

  const [patchRecipient, { isLoading: isPatchLoading }] = usePatchAdminRecipientMutation();
  const [createCandidate, { isLoading: isCandidateLoading }] = useCreateCandidateMutation();
  const [createRace] = useCreateRaceMutation();
  const [duplicateModal, setDuplicateModal] = useState<DuplicateModalState>(CLOSED_MODAL);
  const [chariotModal, setChariotModal] = useState<ChariotModalState>(CLOSED_CHARIOT_MODAL);
  const [triggerLookup] = useLazyLookupRecipientsQuery();
  const lastLookedUpNameRef = useRef<string>('');
  const formRef = useRef<FormApi<RecipientFormValues> | null>(null);

  useEffect(() => {
    if (recipientDetail?.display_name) {
      lastLookedUpNameRef.current = recipientDetail.display_name;
    }
  }, [recipientDetail?.display_name]);

  useEffect(() => {
    if (recipientDetail?.type) {
      setCurrentType(recipientDetail.type);
      setTabStatus(
        recipientDetail.type === 'CANDIDATE' ? ['in-progress', 'in-progress'] : ['in-progress']
      );
    }
  }, [recipientDetail?.type]);

  const initialValues = useMemo((): Partial<RecipientFormValues> => {
    if (isEditMode && recipientDetail) {
      return {
        recipient_name: recipientDetail.display_name,
        recipient_legal_name: recipientDetail.legal_name ?? '',
        type: recipientDetail.type,
        subtype: recipientDetail.subtype ?? SubtypeOptions[0]?.value,
        recipient_photo_id: recipientDetail.misc_ids?.photo_id ?? '',
        recipient_de_url_id: recipientDetail.misc_ids?.ext_de_url_id ?? '',
        public_search: recipientDetail.is_public_searchable,
        stripe_authorized: recipientDetail.is_stripe_authorized,
        onboarding_form_complete: recipientDetail.onboarding_form_complete,
        recipient_ein: recipientDetail.legal_id ?? '',
        recipient_contact_name: recipientDetail.primary_contact_name ?? '',
        recipient_contact_email: recipientDetail.primary_contact_email ?? '',
        recipient_status: recipientDetail.recipient_status ?? 'ACTIVE',
        stripe_id: recipientDetail.stripe_id ?? '',
        de_status: recipientDetail.de_status ?? '',
        recipient_de_api_id: recipientDetail.de_id ?? '',
        public_fundraising_query_str: recipientDetail.public_fundraising_query_str ?? '',
      };
    }
    return {
      public_search: false,
      stripe_authorized: false,
      onboarding_form_complete: false,
      recipient_status: 'ACTIVE',
      type: TypeOptions[0]?.value,
      subtype: SubtypeOptions[0]?.value,
    };
  }, [isEditMode, recipientDetail]);

  const checkDuplicateName = useCallback(
    async (value: string, entityType: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      lastLookedUpNameRef.current = trimmed;
      const lookupEntityType = entityType === 'CANDIDATE' ? 'CANDIDATE' : undefined;
      try {
        const matches = await triggerLookup({
          displayName: trimmed,
          ...(recipientId && { excludeRecipientId: recipientId }),
          ...(lookupEntityType && { entityType: lookupEntityType }),
        }).unwrap();
        if (matches.length > 0) {
          setDuplicateModal({
            isOpen: true,
            duplicates: matches,
            duplicateType: 'display_name',
            pendingValues: null,
            pendingOpts: {},
          });
        }
      } catch {
        // silently ignore lookup errors — don't block the user
      }
    },
    [triggerLookup, recipientId]
  );

  const handleTypeChange = (newType: string) => {
    setCurrentType(newType);
    const isCandidate = newType === 'CANDIDATE';
    setTabStatus(isCandidate ? ['in-progress', 'in-progress'] : ['in-progress']);
    setItemVisible(0);

    const currentName = formRef.current?.getState().values.recipient_name;
    if (currentName) checkDuplicateName(currentName, newType);
  };

  const submitRecipient = async (values: RecipientFormValues, confirmDuplicate?: boolean) => {
    const body = buildBody(values);
    await dispatch(createRecipient(body, activeRole, confirmDuplicate));
    trackRecipientSaved('create', values.type);
    dispatch(adminRecipientsApi.util.invalidateTags(['AdminRecipients']));
    showToast('Success! Your non profit recipient has been created', 'success');
    navigate('/recipients');
  };

  const submitCandidate = async (values: RecipientFormValues, confirmDuplicate?: boolean) => {
    // Phase 1: API accepts a single election. The picker stores multiple for future support.
    const firstElection = values.elections_selected?.[0];
    if (!firstElection) return;
    try {
      const response = await createCandidate({
        body: {
          display_name: values.recipient_name,
          legal_name: values.recipient_legal_name,
          subtype: values.subtype,
          photo_id: values.recipient_photo_id,
          de_url_id: values.recipient_de_url_id,
          is_public_searchable: values.public_search,
          election_id: firstElection.election_id,
          ...(firstElection?.is_incumbent && { is_incumbent: true }),
          ...(firstElection?.is_self_funded && { is_self_funded: true }),
          ...(firstElection?.is_pay_to_play_issue && { is_pay_to_play_issue: true }),
          ...(firstElection?.status && { status: firstElection.status }),
          ...(values.recipient_contact_name && {
            primary_contact_name: values.recipient_contact_name,
          }),
          ...(values.recipient_contact_email && {
            primary_contact_email: values.recipient_contact_email,
          }),
          ...(values.recipient_status && { recipient_status: values.recipient_status }),
          ...(values.stripe_authorized && { is_stripe_authorized: true }),
          ...(values.onboarding_form_complete && { onboarding_form_complete: true }),
          ...(values.de_status && { de_status: values.de_status }),
          ...(values.public_fundraising_query_str && {
            public_fundraising_query_str: values.public_fundraising_query_str,
          }),
        },
        activeRole,
        confirmDuplicate,
      }).unwrap();
      trackRecipientSaved('create', values.type);
      dispatch(adminRecipientsApi.util.invalidateTags(['AdminRecipients']));
      showToast(
        `Candidate recipient created successfully — Entity ID: ${response.entity_id}, Recipient ID: ${response.recipient_id}, Race ID: ${response.race_id}`,
        'success'
      );
      navigate('/recipients');
    } catch (error) {
      const err = error as {
        status?: number;
        data?: { duplicate_type?: string; reason?: string; recipients?: LookupRecipient[] };
      };
      if (err?.status === 409 && err.data?.duplicate_type === 'display_name') {
        setDuplicateModal({
          isOpen: true,
          duplicates: err.data?.recipients ?? [],
          duplicateType: 'display_name',
          pendingValues: values,
          pendingOpts: {},
        });
      } else if (err?.status === 409) {
        showToast(
          err.data?.reason ??
            'This candidate is already linked to this election. Please select a different election.',
          'error'
        );
      } else {
        datadogLogs.logger.error(
          'Failed to create candidate',
          { status: err?.status },
          error as Error
        );
        showToast('Something went wrong. Please try again.', 'error');
      }
    }
  };

  const addNewElection = async (
    targetRecipientId: string,
    election: SelectedElectionItem
  ): Promise<boolean> => {
    try {
      const response = await createRace({
        recipientId: targetRecipientId,
        body: {
          election_id: election.election_id,
          status: election.status ?? 'ACTIVE',
          is_incumbent: election.is_incumbent,
          is_self_funded: election.is_self_funded,
          is_pay_to_play_issue: election.is_pay_to_play_issue,
        },
      }).unwrap();
      showToast(`Added ${election.display_info} — Race ID: ${response.race_id}`, 'success');
      formRef.current?.change('new_election', undefined);
      return true;
    } catch (error) {
      const err = error as { status?: number; data?: { reason?: string } };
      if (err?.status === 409) {
        showToast(
          err.data?.reason ?? 'This candidate is already linked to this election.',
          'error'
        );
      } else {
        datadogLogs.logger.error('Failed to add race', { status: err?.status }, error as Error);
        showToast('Something went wrong adding the election. Please try again.', 'error');
      }
      return false;
    }
  };

  const submitPatch = async (values: RecipientFormValues, opts: PatchOpts = {}) => {
    if (!recipientId) return;
    const body = buildPatch(initialValues, values);
    try {
      await patchRecipient({ recipientId, body, ...opts }).unwrap();
      trackRecipientSaved('edit', values.type);
      if (values.new_election) {
        const added = await addNewElection(recipientId, values.new_election);
        if (!added) return;
      }
      showToast('Recipient updated successfully', 'success');
      navigate('/recipients');
    } catch (error) {
      const err = error as {
        status?: number;
        data?: DuplicateConflictPayload & { reason?: string };
      };
      if (err?.status === 409 && err.data?.duplicate_type) {
        setDuplicateModal({
          isOpen: true,
          duplicates: err.data?.recipients ?? [],
          duplicateType: err.data.duplicate_type,
          pendingValues: values,
          pendingOpts: opts,
        });
      } else if (err?.status === 409) {
        showToast(
          err.data?.reason ?? "This change conflicts with the recipient's nonprofit registration.",
          'error'
        );
      } else if (err?.status === 412) {
        const reason = err.data?.reason ?? 'Chariot compliance check failed. Please try again.';
        showToast(reason, 'error');
      } else if (err?.status === 403) {
        showToast("You don't have permission to update this recipient.", 'error');
      } else if (err?.status === 404) {
        const chariotNonprofitMissing = err.data?.reason === 'Chariot nonprofit not found';
        showToast(
          chariotNonprofitMissing
            ? "That EIN isn't a recognized nonprofit in DAFPay — double-check it."
            : 'Recipient not found.',
          'error'
        );
      } else if (err?.status === 422) {
        showToast('Invalid data. Please check the form and try again.', 'error');
      } else {
        datadogLogs.logger.error(
          'Failed to PATCH admin recipient',
          { status: err?.status },
          error as Error
        );
        showToast('Something went wrong. Please try again.', 'error');
      }
    }
  };

  const handleEditSubmit = (values: RecipientFormValues, opts: PatchOpts = {}) => {
    const miscIds = recipientDetail?.misc_ids;
    const hasChariot = hasChariotKeys(miscIds);

    if (hasChariot && subtypeChangedToNonC3(initialValues, values) && !opts.clearChariotKeys) {
      setChariotModal({ type: 'subtype', pendingValues: values, pendingOpts: opts });
      return;
    }
    if (hasChariot && einChangedToNonNull(initialValues, values) && !opts.reregisterChariot) {
      setChariotModal({ type: 'ein', pendingValues: values, pendingOpts: opts });
      return;
    }
    return submitPatch(values, opts);
  };

  const handleChariotSubtypeConfirm = () => {
    const { pendingValues, pendingOpts } = chariotModal;
    setChariotModal(CLOSED_CHARIOT_MODAL);
    if (!pendingValues) return;
    handleEditSubmit(pendingValues, { ...pendingOpts, clearChariotKeys: true });
  };

  const handleChariotSubtypeCancel = () => {
    setChariotModal(CLOSED_CHARIOT_MODAL);
    formRef.current?.change('subtype', initialValues.subtype);
  };

  const handleChariotEINConfirm = () => {
    const { pendingValues, pendingOpts } = chariotModal;
    setChariotModal(CLOSED_CHARIOT_MODAL);
    if (!pendingValues) return;
    return submitPatch(pendingValues, { ...pendingOpts, reregisterChariot: true });
  };

  const handleChariotEINCancel = () => {
    setChariotModal(CLOSED_CHARIOT_MODAL);
    formRef.current?.change('recipient_ein', initialValues.recipient_ein);
  };

  const saveForm = async (values: RecipientFormValues) => {
    if (isEditMode) {
      return handleEditSubmit(values);
    }
    if (values.type === 'CANDIDATE') {
      return submitCandidate(values);
    }
    return new Promise<void>((resolve, reject) => {
      submitRecipient(values)
        .then(resolve)
        .catch((error: unknown) => {
          const parsed = parseCreate409(error);
          if (parsed) {
            setDuplicateModal({
              isOpen: true,
              ...parsed,
              pendingValues: values,
              pendingOpts: {},
            });
            resolve(undefined);
          } else {
            reject(error);
          }
        });
    });
  };

  const closeDuplicateModal = () => setDuplicateModal(CLOSED_MODAL);

  const handleContinueAnyway = async () => {
    const { pendingValues, pendingOpts } = duplicateModal;
    closeDuplicateModal();
    if (!pendingValues) return;
    try {
      if (isEditMode) {
        await submitPatch(pendingValues, { ...pendingOpts, confirmDuplicate: true });
      } else if (pendingValues.type === 'CANDIDATE') {
        await submitCandidate(pendingValues, true);
      } else {
        await submitRecipient(pendingValues, true);
      }
    } catch (error) {
      const parsed = parseCreate409(error);
      if (parsed) {
        setDuplicateModal({
          isOpen: true,
          ...parsed,
          pendingValues,
          pendingOpts: {},
        });
      }
    }
  };

  const handleViewExisting = (recipientId: string) => {
    closeDuplicateModal();
    navigate(`/recipients_detail?recipient_id=${recipientId}`);
  };

  const handleDisplayNameBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      if (!value || value === lastLookedUpNameRef.current) return;
      checkDuplicateName(value, currentType);
    },
    [checkDuplicateName, currentType]
  );

  const validateForm = (values: RecipientFormValues) => validateRecipientForm(values, isEditMode);

  const updateStatus = (item: number, status: string) => {
    setTabStatus(prevStatus =>
      prevStatus.map((s: string, index: number) => (index === item ? status : s))
    );
  };

  const handleNextTab = () => {
    const { errors } = formRef.current?.getState() ?? {};
    updateStatus(0, getTabErrorStatus(0, errors));
    setItemVisible(1);
  };

  const handleClickAccordion = (item: number, form: FormApi<RecipientFormValues>) => {
    setItemVisible(prev => (prev === item ? -1 : item));

    const { errors } = form.getState();
    updateStatus(item, getTabErrorStatus(item, errors));
  };

  if (activeRole && !has_permission('RECIPIENTS_DETAIL_ROUTE', 'R', activeRole)) {
    return (
      <div className="content">
        <h1 className="title-permission">
          Sorry, you don&apos;t have permission to see this screen.
        </h1>
      </div>
    );
  }

  if (isEditMode && (isDetailLoading || (!recipientDetail && !isDetailError))) {
    return <Loader type="dots" />;
  }

  if (isDetailError) {
    const status = (detailError as FetchBaseQueryError)?.status;
    const message =
      status === 404
        ? 'Recipient not found.'
        : status === 403
          ? "You don't have permission to view this recipient."
          : 'Something went wrong. Please try again.';
    return (
      <div className="content">
        <h1 className="title-permission">{message}</h1>
      </div>
    );
  }

  const chariotSubtypeMessage =
    'This organization currently has DAFPay enabled. Changing its subtype will remove this functionality. Proceed?';

  const chariotEINMessage = `This organization is registered with DAFPay using EIN ${initialValues.recipient_ein ?? ''}. Changing the EIN will re-register it with the new EIN. Proceed?`;

  const recType = currentType === 'CANDIDATE' ? 'candidate' : 'nonprofit';
  const tabs = recipientTypes[recType];

  return (
    <>
      {(recipientsState.isLoading || isPatchLoading || isCandidateLoading) && (
        <Loader type="dots" />
      )}
      <ChariotConfirmModal
        isOpen={chariotModal.type === 'subtype'}
        message={chariotSubtypeMessage}
        onConfirm={handleChariotSubtypeConfirm}
        onCancel={handleChariotSubtypeCancel}
      />
      <ChariotConfirmModal
        isOpen={chariotModal.type === 'ein'}
        message={chariotEINMessage}
        onConfirm={handleChariotEINConfirm}
        onCancel={handleChariotEINCancel}
      />
      <DuplicateWarningModal
        isOpen={duplicateModal.isOpen}
        duplicates={duplicateModal.duplicates}
        duplicateType={duplicateModal.duplicateType}
        onCancel={closeDuplicateModal}
        onContinueAnyway={
          duplicateModal.duplicateType !== 'ein' && Boolean(duplicateModal.pendingValues)
            ? handleContinueAnyway
            : undefined
        }
        onViewExisting={handleViewExisting}
      />
      <Form
        onSubmit={saveForm}
        validate={validateForm}
        initialValues={initialValues}
        render={({ handleSubmit, form }) => {
          formRef.current = form;

          const handleFormSubmit = (event?: React.SyntheticEvent) => {
            handleSubmit(event);
            const { errors, hasValidationErrors } = form.getState();
            if (hasValidationErrors) {
              showToast('Please complete the required fields before saving.', 'warning');
              setTabStatus(prev => prev.map((_, index) => getTabErrorStatus(index, errors)));
            }
          };

          return (
            <>
              {!has_permission('RECIPIENTS_DETAIL_ROUTE', 'R', activeRole) ? (
                <div className="content">
                  <h1 className="title-permission">
                    Sorry, you don&apos;t have permission to see this screen.
                  </h1>
                </div>
              ) : (
                <>
                  <DetailHeader
                    saveForm={handleFormSubmit}
                    type="Recipient"
                    value={isEditMode ? 'Update' : 'Create'}
                    isNew={!isEditMode}
                    backTo="/recipients"
                  />
                  <div className="content-full-width">
                    <form onSubmit={handleFormSubmit}>
                      {tabs.map((type, index) => (
                        <div key={index}>
                          <TabAccordion
                            index={index}
                            itemVisible={itemVisible}
                            type={type}
                            tabStatus={tabStatus}
                            handleClickAccordion={() => handleClickAccordion(index, form)}
                          />
                          <RecipientAccordion
                            index={index}
                            itemVisible={itemVisible}
                            formState={form}
                            isLoading={recipientsState.isLoading}
                            isEditMode={isEditMode}
                            recipientId={recipientId ?? undefined}
                            onDisplayNameBlur={handleDisplayNameBlur}
                            onTypeChange={handleTypeChange}
                            onNextTab={handleNextTab}
                          />
                        </div>
                      ))}
                    </form>
                  </div>
                </>
              )}
            </>
          );
        }}
      />
    </>
  );
};
